/**
 * Hearing a demo call, rather than reading an account of it.
 *
 * THERE IS NO `<audio>` ELEMENT IN THE TREE. The element is a plain `new Audio()` in a ref:
 * it is the playback engine and never a control. The browser's own player is a slab of
 * somebody else's design language, and it only reaches its full size once it has bytes - so
 * the row it sits in would change shape the moment you pressed play.
 *
 * THE BYTES ARE FETCHED, NOT POINTED AT. The playback route is behind the console gate, so
 * `src` would need the cookie the split-origin dev setup does not send on a media load - and
 * a failure has to become a sentence rather than a button that silently does nothing.
 *
 * ONE CALL PLAYS AT A TIME, and that is now structural rather than bookkeeping: exactly one
 * of these exists, inside the call modal, and closing the modal unmounts it. A list of
 * twenty calls where two are talking over each other is a list nobody can use.
 */

import { useEffect, useRef, useState } from "react";
import { blob } from "./api";
import { duration } from "./ui";

interface Props {
  /** `/v1/demos/:id/calls/:callId/recording?v=<sha256>` - built by the caller from the row. */
  path: string;
  /** Known before a byte is fetched, so the clock is honest on first paint. */
  durationMs: number;
  playing: boolean;
  onPlay: () => void;
  onStop: () => void;
  /** Mounted by a press of play rather than a press of the row, so start rather than wait. */
  autoStart?: boolean;
}

export function RecordingPlayer({ path, durationMs, playing, onPlay, onStop, autoStart }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [at, setAt] = useState(0);
  const [total, setTotal] = useState(durationMs / 1000);

  const element = useRef<HTMLAudioElement | null>(null);
  const objectUrl = useRef<string | null>(null);

  // The object URL outlives the fetch and nothing else revokes it.
  useEffect(() => () => {
    element.current?.pause();
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
  }, []);

  // The parent stopped us because another row started. `playing` is the source of truth.
  useEffect(() => {
    if (!playing) element.current?.pause();
  }, [playing]);

  async function load(): Promise<HTMLAudioElement | null> {
    if (element.current) return element.current;
    setLoading(true);
    setError(null);
    try {
      const bytes = await blob(path);
      const url = URL.createObjectURL(bytes);
      objectUrl.current = url;
      const audio = new Audio(url);
      audio.addEventListener("timeupdate", () => setAt(audio.currentTime));
      audio.addEventListener("loadedmetadata", () => {
        if (Number.isFinite(audio.duration)) setTotal(audio.duration);
      });
      audio.addEventListener("ended", () => { setAt(0); onStop(); });
      element.current = audio;
      return audio;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the recording");
      return null;
    } finally {
      setLoading(false);
    }
  }

  async function toggle(auto = false) {
    if (playing) {
      element.current?.pause();
      onStop();
      return;
    }
    const audio = await load();
    if (!audio) return;
    onPlay();
    try {
      await audio.play();
    } catch {
      // A refused AUTOSTART is not a broken recording. The bytes arrived, the transport is
      // on screen, and the press that opens the modal is a gesture some browsers will not
      // let us spend across the await - so leave it stopped and let the reader press play.
      if (!auto) setError("This browser would not play the recording");
      onStop();
    }
  }

  // Mount only: whoever mounted us did so by pressing play.
  useEffect(() => {
    if (autoStart) void toggle(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function seek(fraction: number) {
    const audio = await load();
    if (!audio || !Number.isFinite(audio.duration)) return;
    audio.currentTime = audio.duration * fraction;
    setAt(audio.currentTime);
  }

  const done = total > 0 ? Math.min(at / total, 1) : 0;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-3">
        <PlayStop playing={playing} loading={loading} onClick={toggle} label="this call" />
        <div
          role="slider"
          tabIndex={0}
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={Math.round(total)}
          aria-valuenow={Math.round(at)}
          className="relative flex-1 h-1.5 bg-paper-soft border border-field rounded-full cursor-pointer"
          onClick={(e) => {
            const box = e.currentTarget.getBoundingClientRect();
            void seek((e.clientX - box.left) / box.width);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") void seek(Math.min(done + 0.05, 1));
            if (e.key === "ArrowLeft") void seek(Math.max(done - 0.05, 0));
          }}
        >
          <div className="absolute inset-y-0 left-0 bg-ok rounded-full" style={{ width: `${done * 100}%` }} />
        </div>
        <span className="text-[12px] text-muted tabular-nums">
          {duration(at * 1000)} / {duration(total * 1000)}
        </span>
      </div>
      {error && <span className="text-[12px] text-warn">{error}</span>}
    </div>
  );
}

/**
 * The 32px square in a call row - the same engine, none of the transport.
 *
 * A row's play control is NOT a `<button>` inside the row's own click target, so the square
 * stops propagation and does its own thing. On the calls list it opens the call modal with
 * the audio already running; here in the transport it is the transport's own play.
 */
export function PlayStop({ playing, loading, onClick, label }: {
  playing: boolean; loading: boolean; onClick: () => void; label: string;
}) {
  return (
    <button
      type="button"
      aria-label={playing ? `Stop ${label}` : `Play ${label}`}
      disabled={loading}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full border border-field bg-paper-soft text-ok hover:border-ok disabled:opacity-50"
    >
      {loading
        ? <span className="block w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
        : playing
          ? <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><rect width="10" height="10" rx="1.5" fill="currentColor" /></svg>
          : <svg width="10" height="12" viewBox="0 0 10 12" aria-hidden="true"><path d="M0 0l10 6-10 6z" fill="currentColor" /></svg>}
    </button>
  );
}
